/**
 * Central authentication service — single integration point for all login portals.
 * Matches backend POST /auth/login-email and GET /auth/me.
 */
import { apiService } from './apiService';
import { prefetchBootstrapLookups } from './bootstrapService';
import { RBACService, User } from '../utils/rbac';
import type {
  AuthMeResponse,
  LoginEmailRequest,
  LoginEmailResponse,
  LoginMode,
} from '../types/api';

export interface PortalLoginResult {
  token: string;
  user: User;
  /** Wrong portal for role — caller should show message and redirect */
  portalMismatch?: { message: string; redirectTo: string };
}

function extractUsername(email: string, username?: string): string {
  return username?.trim() || email.split('@')[0];
}

function displayName(data: LoginEmailResponse, fallbackEmail: string): string {
  if (data.first_name && data.last_name) {
    return `${data.first_name} ${data.last_name}`.trim();
  }
  return extractUsername(data.email || fallbackEmail, data.username);
}

function hasPermissionList(profile: AuthMeResponse | LoginEmailResponse): boolean {
  const rolePerms = 'role' in profile ? profile.role?.permissions : undefined;
  const direct = 'permissions' in profile ? profile.permissions : undefined;
  const effective = 'effective_permissions' in profile ? profile.effective_permissions : undefined;
  return (
    (Array.isArray(rolePerms) && rolePerms.length > 0) ||
    (Array.isArray(direct) && direct.length > 0) ||
    (Array.isArray(effective) && effective.length > 0)
  );
}

async function fetchEffectivePermissions(): Promise<string[]> {
  const me = await apiService.get<AuthMeResponse>('/auth/me');
  return me?.role?.permissions || me?.permissions || me?.effective_permissions || [];
}

function buildSessionUser(
  data: LoginEmailResponse,
  fallbackEmail: string,
  permissions: string[],
): User {
  const roleName = data.role_name || '';
  const roleId = Number.isFinite(Number(data.role_id)) ? Number(data.role_id) : 1;
  return {
    id: String(data.user_id ?? ''),
    email: data.email || fallbackEmail,
    name: displayName(data, fallbackEmail),
    roleId,
    roleName,
    permissions,
    locationId: data.location_id ?? null,
    locationName: data.location_name ?? null,
    companyId: data.company_id ?? null,
    companyPlanType: data.company_plan_type ?? null,
    profileImagePath: data.profile_image_path ?? null,
  };
}

/** Persist JWT + user profile to localStorage and RBAC session. */
export function persistAuthSession(user: User, token: string): void {
  localStorage.setItem('authToken', token);
  localStorage.setItem('isAuthenticated', 'true');
  localStorage.setItem('userEmail', user.email);
  localStorage.setItem('userRole', user.roleName);
  localStorage.setItem('userRoleId', String(user.roleId));
  localStorage.setItem('userId', user.id);
  localStorage.setItem('userName', user.name);
  RBACService.setCurrentUser(user);
  window.dispatchEvent(new CustomEvent('sessionRestored'));
  prefetchBootstrapLookups();
}

/** Map axios/FastAPI errors to user-facing login messages. */
export function parseLoginError(error: unknown, fallbackEmail?: string): string {
  const err = error as {
    message?: string;
    code?: string;
    response?: { status?: number; data?: { detail?: unknown } };
  };
  const detail = err?.response?.data?.detail;
  const detailStr =
    typeof detail === 'string'
      ? detail
      : Array.isArray(detail)
        ? detail.map((d: { msg?: string }) => d.msg).filter(Boolean).join(', ')
        : '';

  const status = err?.response?.status;
  if (status === 403) return detailStr || 'Use the correct portal or contact your administrator.';
  if (status === 401) return detailStr || 'Invalid email or password.';
  if (status === 400) return detailStr || 'Account is inactive or invalid.';
  if (status === 503) return detailStr || 'Service temporarily unavailable. Try again shortly.';
  if (status === 500) return detailStr || 'Server error. Please try again later.';
  if (err?.message === 'Network Error' || err?.code === 'ERR_NETWORK') {
    return 'Unable to connect to the server. Ensure the backend is running.';
  }
  if (err?.code === 'ECONNABORTED' || err?.message?.toLowerCase().includes('timeout')) {
    return 'Request timed out. The server may be starting — wait a moment and try again.';
  }
  if (err?.message?.includes('Access denied')) return err.message;
  return err?.message || `Sign-in failed${fallbackEmail ? ` for ${fallbackEmail}` : ''}.`;
}

/**
 * Login via backend login-email endpoint, load permissions, validate portal role.
 */
export async function loginWithEmail(
  email: string,
  password: string,
  loginMode: LoginMode,
  options?: { skipPermissionFetch?: boolean; defaultPermissions?: string[] },
): Promise<PortalLoginResult> {
  const body: LoginEmailRequest = { email, password, login_mode: loginMode };
  const data = await apiService.post<LoginEmailResponse>('/auth/login-email', body);

  if (!data?.access_token) {
    throw new Error('Invalid login response from server');
  }

  localStorage.setItem('authToken', data.access_token);

  let permissions = options?.defaultPermissions ?? [];
  if (!options?.skipPermissionFetch && !hasPermissionList(data)) {
    try {
      permissions = await fetchEffectivePermissions();
    } catch {
      permissions = options?.defaultPermissions ?? [];
    }
  }

  const user = buildSessionUser(data, email, permissions);
  const roleLower = (user.roleName || '').toLowerCase();

  if (loginMode === 'admin' && roleLower !== 'admin') {
    throw new Error('This portal is only for Admin users.');
  }
  if (loginMode === 'superadmin' && roleLower !== 'super admin') {
    throw new Error('Access denied. This login page is for System Owner only.');
  }

  let portalMismatch: PortalLoginResult['portalMismatch'];
  if (loginMode === 'user') {
    if (roleLower === 'super admin') {
      portalMismatch = {
        message: 'System Owner must login from the System Owner login page.',
        redirectTo: '/superadmin/login',
      };
    } else if (roleLower === 'admin') {
      portalMismatch = {
        message: 'Admins must sign in from the Admin Portal.',
        redirectTo: '/admin/login',
      };
    }
  }

  return { token: data.access_token, user, portalMismatch };
}

export async function logout(): Promise<void> {
  localStorage.removeItem('authToken');
  localStorage.removeItem('isAuthenticated');
  RBACService.clearUser();
}
